<template>
  <div class="chat-tab">
    <div class="chat-container">
      <!-- Header -->
      <div class="chat-header">
        <div class="header-content">
          <h1 class="chat-title">{{ currentTask === 'brand_setup' ? 'Brand Discovery Chat' : 'New Project Chat' }}</h1>
          <p class="chat-subtitle">{{ currentTask === 'brand_setup' ? 'Let\'s discover and fill out your brand information together' : 'Let\'s create a new project together' }}</p>
        </div>
      </div>

      <!-- Task Selection Screen -->
      <div v-if="!currentTask" class="task-selection">
        <n-card title="What would you like to do?" style="max-width: 600px; margin: 0 auto;">
          <div class="task-options">
            <n-card 
              hoverable 
              class="task-option"
              @click="selectTask('brand_setup')"
            >
              <h3>Initial Brand Setup</h3>
              <p>Discover and fill out your brand information</p>
            </n-card>
            <n-card
              hoverable
              class="task-option"
              @click="selectTask('new_project')"
            >
              <h3>New Project</h3>
              <p>Create a new project with deliverables and assets</p>
            </n-card>
            <n-card class="task-option task-option--disabled" style="opacity: 0.55; cursor: not-allowed; pointer-events: none;">
              <h3>Expand Brand Setup</h3>
              <p>Add more detail or update an existing brand profile</p>
              <span class="coming-soon-label">Coming soon</span>
            </n-card>
            <n-card class="task-option task-option--disabled" style="opacity: 0.55; cursor: not-allowed; pointer-events: none;">
              <h3>Refine Existing Brief</h3>
              <p>Revisit and polish a project brief you've already started</p>
              <span class="coming-soon-label">Coming soon</span>
            </n-card>
            <n-card class="task-option task-option--disabled" style="opacity: 0.55; cursor: not-allowed; pointer-events: none;">
              <h3>Explore Ideas</h3>
              <p>Brainstorm creative directions and concepts</p>
              <span class="coming-soon-label">Coming soon</span>
            </n-card>
          </div>
        </n-card>
      </div>

      <!-- Main Content: Chat and Mini Brief -->
      <div v-if="currentTask" class="main-content">
        <!-- Chat Messages Area -->
        <div class="chat-section">
          <div class="chat-messages" ref="messagesContainer">
            <template v-for="(msg, index) in messages" :key="index">
              <div
                :class="['message', msg.role]"
              >
                <div class="message-avatar-wrapper">
                  <div class="message-avatar" v-if="msg.role === 'bot'">
                    <n-icon size="20">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,2A2,2 0 0,1 14,4C14,4.74 13.6,5.39 13,5.73V7H14A7,7 0 0,1 21,14H22A1,1 0 0,1 23,15V18A1,1 0 0,1 22,19H21A7,7 0 0,1 14,26H10A7,7 0 0,1 3,19H2A1,1 0 0,1 1,18V15A1,1 0 0,1 2,14H3A7,7 0 0,1 10,7H11V5.73C10.4,5.39 10,4.74 10,4A2,2 0 0,1 12,2M7.5,13A2.5,2.5 0 0,0 5,15.5A2.5,2.5 0 0,0 7.5,18A2.5,2.5 0 0,0 10,15.5A2.5,2.5 0 0,0 7.5,13M16.5,13A2.5,2.5 0 0,0 14,15.5A2.5,2.5 0 0,0 16.5,18A2.5,2.5 0 0,0 19,15.5A2.5,2.5 0 0,0 16.5,13Z" />
                      </svg>
                    </n-icon>
                  </div>
                  <div class="message-avatar" v-if="msg.role === 'user'">
                    <n-icon size="20">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,12C14.67,12 17,9.76 17,7C17,4.24 14.67,2 12,2C9.33,2 7,4.24 7,7C7,9.76 9.33,12 12,12M12,14C9.33,14 4,15.34 4,18V20H20V18C20,15.34 14.67,14 12,14Z" />
                      </svg>
                    </n-icon>
                  </div>
                  <div class="message-time">{{ formatTime(msg.timestamp) }}</div>
                </div>
                <div class="message-content">
                  <div class="message-text" v-html="formatMessage(msg.text)"></div>
                </div>
              </div>
              <hr v-if="index < messages.length - 1" class="message-separator" />
            </template>

            <!-- Loading indicator -->
            <div v-if="isLoading" class="message bot">
              <div class="message-avatar">
                <n-icon size="20">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2A2,2 0 0,1 14,4C14,4.74 13.6,5.39 13,5.73V7H14A7,7 0 0,1 21,14H22A1,1 0 0,1 23,15V18A1,1 0 0,1 22,19H21A7,7 0 0,1 14,26H10A7,7 0 0,1 3,19H2A1,1 0 0,1 1,18V15A1,1 0 0,1 2,14H3A7,7 0 0,1 10,7H11V5.73C10.4,5.39 10,4.74 10,4A2,2 0 0,1 12,2M7.5,13A2.5,2.5 0 0,0 5,15.5A2.5,2.5 0 0,0 7.5,18A2.5,2.5 0 0,0 10,15.5A2.5,2.5 0 0,0 7.5,13M16.5,13A2.5,2.5 0 0,0 14,15.5A2.5,2.5 0 0,0 16.5,18A2.5,2.5 0 0,0 19,15.5A2.5,2.5 0 0,0 16.5,13Z" />
                  </svg>
                </n-icon>
              </div>
              <div class="message-content">
                <div class="message-text">
                  <n-spin size="small" /> Thinking...
                </div>
              </div>
            </div>
          </div>

          <!-- Input Area -->
          <div class="chat-input-area">
        <!-- New Project: Project Type -->
        <div v-if="currentTask === 'new_project' && currentStep === 'project_type'" class="structured-input">
          <div class="project-type-buttons">
            <n-button
              :type="projectType === 'photo_post' ? 'primary' : 'default'"
              size="large"
              @click="selectProjectType('photo_post')"
              style="flex: 1;"
            >
              Photo Post
            </n-button>
            <n-button
              :type="projectType === 'video' ? 'primary' : 'default'"
              size="large"
              @click="selectProjectType('video')"
              style="flex: 1;"
            >
              Video
            </n-button>
            <n-button
              :type="projectType === 'toolkit' ? 'primary' : 'default'"
              size="large"
              @click="selectProjectType('toolkit')"
              style="flex: 1;"
            >
              Toolkit
            </n-button>
          </div>
        </div>

        <!-- New Project: Photo Post - Aspect Ratio -->
        <div v-if="currentTask === 'new_project' && currentStep === 'photo_aspect_ratio'" class="structured-input">
          <div class="project-type-buttons">
            <n-button
              :type="photoAspectRatio === 'square' ? 'primary' : 'default'"
              size="large"
              @click="selectAspectRatio('square')"
              style="flex: 1;"
            >
              Square
            </n-button>
            <n-button
              :type="photoAspectRatio === '4x5' ? 'primary' : 'default'"
              size="large"
              @click="selectAspectRatio('4x5')"
              style="flex: 1;"
            >
              4x5
            </n-button>
            <n-button
              :type="photoAspectRatio === 'multiple' ? 'primary' : 'default'"
              size="large"
              @click="selectAspectRatio('multiple')"
              style="flex: 1;"
            >
              Multiple Variations
            </n-button>
          </div>
        </div>

        <!-- New Project: Photo Post - Slides -->
        <div v-if="currentTask === 'new_project' && currentStep === 'photo_slides'" class="structured-input">
          <n-input
            v-model:value="photoSlides"
            placeholder="How many slides do you roughly think you'll need? (e.g., 5-7 or 5 to 7)"
            size="large"
            style="width: 100%"
            @keyup.enter="submitPhotoSlides"
          >
            <template #suffix>
              <n-button
                :loading="isLoading"
                :disabled="!isValidSlideInput(photoSlides)"
                @click="submitPhotoSlides"
                type="primary"
                size="small"
              >
                Next
              </n-button>
            </template>
          </n-input>
        </div>

        <!-- New Project: Photo Post - Content -->
        <div v-if="currentTask === 'new_project' && currentStep === 'photo_content'" class="structured-input">
          <n-input
            v-model:value="photoContent"
            type="textarea"
            :rows="4"
            placeholder="Tell us about the content of the post..."
            size="large"
            @keyup.enter.ctrl="submitPhotoContent"
          />
          <div style="margin-top: 16px; display: flex; justify-content: flex-end;">
            <n-button
              type="primary"
              :disabled="!photoContent.trim()"
              @click="submitPhotoContent"
              size="large"
            >
              Next
            </n-button>
          </div>
        </div>

        <!-- New Project: Photo Post - Assets Needed -->
        <div v-if="currentTask === 'new_project' && currentStep === 'photo_assets_needed'" class="structured-input">
          <p style="margin-bottom: 16px;">Are there additional assets that we need, or does everything already exist in the toolkit?</p>
          <div class="project-type-buttons">
            <n-button
              :type="photoAssetsNeeded === false ? 'primary' : 'default'"
              size="large"
              @click="selectAssetsNeeded(false)"
              style="flex: 1;"
            >
              Everything Exists
            </n-button>
            <n-button
              :type="photoAssetsNeeded === true ? 'primary' : 'default'"
              size="large"
              @click="selectAssetsNeeded(true)"
              style="flex: 1;"
            >
              Need New Assets
            </n-button>
          </div>
        </div>

        <!-- New Project: Photo Post - Assets Source -->
        <div v-if="currentTask === 'new_project' && currentStep === 'photo_assets_source'" class="structured-input">
          <p style="margin-bottom: 16px;">Do you have assets to upload, or are we creating new ones?</p>
          <div class="project-type-buttons">
            <n-button
              :type="photoAssetsSource === 'upload' ? 'primary' : 'default'"
              size="large"
              @click="selectAssetsSource('upload')"
              style="flex: 1;"
            >
              I Have Assets to Upload
            </n-button>
            <n-button
              :type="photoAssetsSource === 'create' ? 'primary' : 'default'"
              size="large"
              @click="selectAssetsSource('create')"
              style="flex: 1;"
            >
              Create New Assets
            </n-button>
          </div>
        </div>

        <!-- New Project: Deliverables -->
        <div v-else-if="currentTask === 'new_project' && currentStep === 'deliverables'" class="structured-input">
          <n-input-number
            v-model:value="deliverablesCount"
            placeholder="How many deliverables/variations do you need?"
            size="large"
            :min="1"
            :show-button="false"
            style="width: 100%"
            @keyup.enter="submitDeliverables"
          >
            <template #suffix>
              <n-button
                :loading="isLoading"
                :disabled="!deliverablesCount || deliverablesCount < 1"
                @click="submitDeliverables"
                type="primary"
                size="small"
              >
                Next
              </n-button>
            </template>
          </n-input-number>
        </div>

        <!-- New Project: Asset Upload -->
        <div v-else-if="currentTask === 'new_project' && currentStep === 'asset_upload'" class="structured-input">
          <n-upload
            v-model:file-list="uploadedFiles"
            multiple
            :max="50"
            directory-dnd
          >
            <n-upload-dragger>
              <div style="padding: 20px;">
                <p>Drag and drop files here, or click to select</p>
                <p style="font-size: 12px; color: var(--text-color-2); margin-top: 8px;">
                  You can also skip this step and upload assets later
                </p>
              </div>
            </n-upload-dragger>
          </n-upload>
          <div style="margin-top: 16px; display: flex; gap: 12px; justify-content: flex-end;">
            <n-button @click="skipAssetUpload">Skip</n-button>
            <n-button type="primary" @click="submitAssetUpload" :loading="isLoading">
              Continue
            </n-button>
          </div>
        </div>

        <!-- New Project: Confirm & Create -->
        <div v-else-if="currentTask === 'new_project' && currentStep === 'confirm_create'" class="confirmation-panel">
          <n-card title="Project Details" style="margin-bottom: 16px;">
            <n-form :model="{ projectName, projectCode, projectDescription }" label-placement="top">
              <n-form-item label="Project Name" required>
                <n-input v-model:value="projectName" placeholder="e.g., Summer Campaign 2024" />
              </n-form-item>
              <n-form-item label="Project Code" required>
                <n-input v-model:value="projectCode" placeholder="e.g., summer24" />
              </n-form-item>
              <n-form-item label="Description (optional)">
                <n-input
                  v-model:value="projectDescription"
                  type="textarea"
                  :rows="3"
                  placeholder="Project description"
                />
              </n-form-item>
            </n-form>
          </n-card>
          <div class="confirmation-actions">
            <n-button @click="goBackToAssetUpload">Back</n-button>
            <n-button type="primary" @click="createProject" :loading="isSaving" :disabled="!projectName || !projectCode">
              Create Project
            </n-button>
          </div>
        </div>

        <!-- Brand Setup: Structured Input Fields (shown based on conversation step) -->
        <div v-else-if="currentTask === 'brand_setup' && currentStep === 'collect_brand_name'" class="structured-input">
          <n-input
            v-model:value="brandNameInput"
            placeholder="Enter your business or brand name"
            size="large"
            @keyup.enter="submitBrandName"
          >
            <template #suffix>
              <n-button
                :loading="isLoading"
                :disabled="!brandNameInput"
                @click="submitBrandName"
                type="primary"
                size="small"
              >
                Next
              </n-button>
            </template>
          </n-input>
        </div>
        <div v-else-if="currentTask === 'brand_setup' && currentStep === 'collect_website'" class="structured-input">
          <n-input
            v-model:value="websiteInput"
            placeholder="Enter website URL (e.g., https://example.com)"
            size="large"
            @keyup.enter="submitWebsite"
          >
            <template #suffix>
              <n-button
                :loading="isLoading"
                :disabled="!websiteInput"
                @click="submitWebsite"
                type="primary"
                size="small"
              >
                Submit
              </n-button>
            </template>
          </n-input>
        </div>

        <!-- Confirmation UI for discovered info -->
        <div v-else-if="currentTask === 'brand_setup' && currentStep === 'confirm_info'" class="confirmation-panel">
          <n-card title="Discovered Information" style="margin-bottom: 16px;">
            <n-form :model="discoveredInfo" label-placement="left" label-width="120">
              <n-form-item label="Brand Name">
                <n-input v-model:value="discoveredInfo.brandName" placeholder="Brand name" />
              </n-form-item>
              <n-form-item label="Address">
                <n-input v-model:value="discoveredInfo.address" placeholder="Address" />
              </n-form-item>
              <n-form-item label="Phone">
                <n-input v-model:value="discoveredInfo.phone" placeholder="Phone number" />
              </n-form-item>
              <n-form-item label="Email">
                <n-input v-model:value="discoveredInfo.email" placeholder="Email address" />
              </n-form-item>
              <n-form-item label="Instagram Handle">
                <n-input 
                  v-model:value="discoveredInfo.instagramHandle" 
                  placeholder="Instagram handle (without @)"
                >
                  <template #prefix v-if="discoveredInfo.instagramHandle">@</template>
                </n-input>
              </n-form-item>
            </n-form>
          </n-card>

          <!-- Logos and Photos Thumbnails -->
          <n-card v-if="(filteredLogos.length > 0 || filteredPhotos.length > 0)" title="Logos & Photos" style="margin-bottom: 16px;">
            <!-- Logos Section -->
            <div v-if="filteredLogos.length > 0" style="margin-bottom: 24px;">
              <h4 style="margin: 0 0 12px 0; font-size: 14px; font-weight: 600; color: var(--text-color);">Logos</h4>
              <div class="thumbnail-grid">
                <div v-for="logoUrl in filteredLogos" :key="logoUrl" class="thumbnail-wrapper">
                  <img :src="logoUrl" :alt="'Logo'" class="thumbnail-image" />
                  <button class="thumbnail-close" @click="removeImage(logoUrl, 'logo')" type="button">
                    <n-icon size="16">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                      </svg>
                    </n-icon>
                  </button>
                </div>
              </div>
            </div>

            <!-- Photos Section -->
            <div v-if="filteredPhotos.length > 0">
              <h4 style="margin: 0 0 12px 0; font-size: 14px; font-weight: 600; color: var(--text-color);">Photos</h4>
              <div class="thumbnail-grid">
                <div v-for="photoUrl in filteredPhotos" :key="photoUrl" class="thumbnail-wrapper">
                  <img :src="photoUrl" :alt="'Photo'" class="thumbnail-image" />
                  <button class="thumbnail-close" @click="removeImage(photoUrl, 'photo')" type="button">
                    <n-icon size="16">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                      </svg>
                    </n-icon>
                  </button>
                </div>
              </div>
            </div>
          </n-card>

          <div class="confirmation-actions">
            <n-button type="primary" @click="confirmInfo" :loading="isLoading">
              Confirm & Continue
            </n-button>
          </div>
        </div>

        <!-- Final confirmation for generated content -->
        <div v-else-if="currentTask === 'brand_setup' && currentStep === 'confirm_generated'" class="confirmation-panel">
          <n-card title="Generated Brand Content" style="margin-bottom: 16px;">
            <n-form :model="generatedContent" label-placement="top">
              <n-form-item label="Brand Code">
                <n-input
                  v-model:value="generatedContent.brandCode"
                  placeholder="Brand code (e.g., MBM26)"
                />
              </n-form-item>
              <n-form-item label="Elevator Pitch">
                <n-input
                  v-model:value="generatedContent.elevatorPitch"
                  type="textarea"
                  :rows="3"
                  placeholder="Elevator pitch"
                />
              </n-form-item>
              <n-form-item label="Voice Tone">
                <n-input
                  v-model:value="generatedContent.voiceTone"
                  type="textarea"
                  :rows="3"
                  placeholder="Voice tone description"
                />
              </n-form-item>
              <n-form-item label="Taglines">
                <div v-for="(_, index) in generatedContent.taglines" :key="index" style="margin-bottom: 8px;">
                  <n-input v-model:value="generatedContent.taglines[index]" placeholder="Tagline" />
                </div>
              </n-form-item>
              <n-form-item label="Positive Keywords">
                <n-dynamic-tags v-model:value="generatedContent.positiveKeywords" />
              </n-form-item>
              <n-form-item label="Negative Keywords">
                <n-dynamic-tags v-model:value="generatedContent.negativeKeywords" />
              </n-form-item>
            </n-form>
          </n-card>
          <div class="confirmation-actions">
            <n-button type="primary" @click="saveBrand" :loading="isSaving">
              Save to Brand
            </n-button>
            <n-button @click="restart">Start Over</n-button>
          </div>
        </div>

        <!-- Default chat input (only for brand setup free-form chat) -->
        <div v-else-if="currentTask === 'brand_setup' && !['collect_brand_name', 'collect_website', 'confirm_info', 'confirm_generated'].includes(currentStep as string)" class="chat-input">
          <n-input
            v-model:value="inputMessage"
            placeholder="Type your message..."
            size="large"
            @keyup.enter="sendMessage"
            :disabled="isLoading"
          >
            <template #suffix>
              <n-button
                :loading="isLoading"
                :disabled="!inputMessage"
                @click="sendMessage"
                type="primary"
                size="small"
              >
                Send
              </n-button>
            </template>
          </n-input>
        </div>
          </div>

          <!-- Progress Indicator -->
          <div class="progress-indicator">
            <n-steps :current="progressStep + 1" :status="progressStatus">
              <n-step 
                v-for="(step, index) in currentTaskSteps" 
                :key="index"
                :title="step.title" 
              />
            </n-steps>
          </div>
        </div>

        <!-- Mini Brief Sidebar -->
        <div v-if="currentTask === 'new_project'" class="mini-brief-section">
          <n-card class="mini-brief-card">
            <div class="mini-brief-content">
              <div v-if="projectType" class="brief-item">
                <div class="brief-label">Project Type</div>
                <div class="brief-value">{{ getProjectTypeLabel(projectType) }}</div>
              </div>
              
              <div v-if="projectType === 'photo_post' && photoAspectRatio" class="brief-item">
                <div class="brief-label">Aspect Ratio</div>
                <div class="brief-value">{{ getAspectRatioLabel(photoAspectRatio) }}</div>
              </div>
              
              <div v-if="photoSlides.trim()" class="brief-item">
                <div class="brief-label">Slides</div>
                <div class="brief-value">
                  {{ photoSlides.includes('-') ? `${photoSlides} slides` : `${photoSlides} slide${photoSlides !== '1' ? 's' : ''}` }}
                </div>
              </div>
              
              <div v-if="photoContent" class="brief-item">
                <div class="brief-label">Content</div>
                <div class="brief-value">{{ photoContent }}</div>
              </div>
              
              <div v-if="photoAssetsNeeded !== null" class="brief-item">
                <div class="brief-label">Assets</div>
                <div class="brief-value">
                  <span v-if="photoAssetsNeeded === false">Everything Exists</span>
                  <span v-else-if="photoAssetsSource === 'upload'">I Have Assets to Upload</span>
                  <span v-else-if="photoAssetsSource === 'create'">Create New Assets</span>
                  <span v-else>Need New Assets</span>
                </div>
              </div>
              
              <div v-if="!projectType && !photoSlides.trim() && !photoContent && photoAssetsNeeded === null" class="brief-empty">
                <div class="brief-empty-text">Your project brief will appear here as you answer questions.</div>
              </div>
            </div>
          </n-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useMessage, NIcon, NInput, NInputNumber, NButton, NSpin, NCard, NForm, NFormItem, NSteps, NStep, NDynamicTags, NUpload, NUploadDragger } from 'naive-ui'
import { apiClient } from '@/services/api'

const message = useMessage()

// Message interface
interface ChatMessage {
  role: 'user' | 'bot'
  text: string
  timestamp: Date
}

// Task management
type TaskType = 'brand_setup' | 'new_project' | null
const currentTask = ref<TaskType>(null)

// Conversation state
const messages = ref<ChatMessage[]>([])
const inputMessage = ref('')
const isLoading = ref(false)
const isSaving = ref(false)

// Brand Setup steps
type BrandSetupStep = 'greeting' | 'collect_brand_name' | 'collect_website' | 'searching' | 'confirm_info' | 'analyzing' | 'confirm_generated' | 'completed'
// New Project steps
type NewProjectStep = 'select_brand' | 'project_type' | 'photo_aspect_ratio' | 'photo_slides' | 'photo_content' | 'photo_assets_needed' | 'photo_assets_source' | 'deliverables' | 'asset_upload' | 'confirm_create' | 'completed'

const currentStep = ref<BrandSetupStep | NewProjectStep>('greeting')
const progressStep = ref(0)
const progressStatus = ref<'process' | 'finish' | 'error'>('process')

// Step definitions for each task
const brandSetupSteps = [
  { title: 'Collect Info' },
  { title: 'Search' },
  { title: 'Analyze' },
  { title: 'Confirm' },
  { title: 'Save' },
]

const currentTaskSteps = computed<Array<{ title: string }>>(() => {
  if (currentTask.value === 'brand_setup') {
    return brandSetupSteps
  }
  if (currentTask.value === 'new_project') {
    if (projectType.value === 'photo_post') {
      return [
        { title: 'Project Type' },
        { title: 'Aspect Ratio' },
        { title: 'Slides' },
        { title: 'Content' },
        { title: 'Assets' },
        { title: 'Create' },
      ]
    }
    return [
      { title: 'Project Type' },
      { title: 'Deliverables' },
      { title: 'Assets' },
      { title: 'Create' },
    ]
  }
  return []
})

// Brand selection
const selectedBrandId = ref<string | null>(null)
const brands = ref<Array<{ id: string; name: string; code: string }>>([])

// Structured inputs (Brand Setup)
const brandNameInput = ref('')
const websiteInput = ref('')
const instagramInput = ref('')

// New Project inputs
const projectType = ref<'photo_post' | 'video' | 'toolkit' | null>(null)
const photoAspectRatio = ref<'square' | '4x5' | 'multiple' | null>(null)
const photoSlides = ref<string>('')
const photoContent = ref('')
const photoAssetsNeeded = ref<boolean | null>(null)
const photoAssetsSource = ref<'upload' | 'create' | null>(null)
const deliverablesCount = ref<number | null>(null)
const uploadedFiles = ref<any[]>([]) // Naive UI Upload file list format
const projectName = ref('')
const projectCode = ref('')
const projectDescription = ref('')

// Discovered information
const discoveredInfo = ref({
  brandName: '',
  address: '',
  phone: '',
  email: '',
  instagramHandle: '',
  socialLinks: [] as string[],
  logoUrls: [] as string[],
  photoUrls: [] as string[],
  colorPalette: null as any,
})

// Track removed images
const removedImageUrls = ref<Set<string>>(new Set())

// Computed properties for filtered images (excluding removed ones)
const filteredLogos = computed(() => {
  return discoveredInfo.value.logoUrls.filter(url => !removedImageUrls.value.has(url))
})

const filteredPhotos = computed(() => {
  return discoveredInfo.value.photoUrls.filter(url => !removedImageUrls.value.has(url))
})

// Generated content
const generatedContent = ref({
  brandCode: '',
  elevatorPitch: '',
  voiceTone: '',
  taglines: [] as string[],
  positiveKeywords: [] as string[],
  negativeKeywords: [] as string[],
  colorPalette: null as any,
  logoUrls: [] as string[],
})

// Website content cache
const websiteContent = ref('')

// Messages container ref for auto-scroll
const messagesContainer = ref<HTMLElement | null>(null)

// Task selection
function selectTask(task: TaskType) {
  currentTask.value = task
  messages.value = []
  progressStep.value = 0
  progressStatus.value = 'process'
  
  if (task === 'brand_setup') {
    startBrandSetup()
  } else if (task === 'new_project') {
    startNewProject()
  }
}

function startBrandSetup() {
  addBotMessage("Hi! I'm here to help you discover and fill out your brand information. Let's start with your business or brand name. What's your brand name?")
  currentStep.value = 'collect_brand_name'
  progressStep.value = 0
}

function startNewProject() {
  // Check localStorage as fallback if component state isn't synced yet
  if (!selectedBrandId.value) {
    const savedBrandId = localStorage.getItem('selectedBrandId')
    if (savedBrandId) {
      selectedBrandId.value = savedBrandId
    }
  }
  
  // Only prompt if truly no brand is selected (should be rare)
  if (!selectedBrandId.value) {
    addBotMessage("Hi! Let's create a new project. Please select a brand first using the dropdown above.")
    return
  }
  
  // Proceed directly to project type - assume selected brand
  const brandName = brands.value.find(b => b.id === selectedBrandId.value)?.name || 'this brand'
  addBotMessage(`Hi! Let's create a new project for ${brandName}. What type of project is this?`)
  currentStep.value = 'project_type'
  progressStep.value = 0
}

// Load brands on mount and sync with Layout component's brand selection
onMounted(async () => {
  try {
    const response = await apiClient.getBrands({ limit: 100 })
    brands.value = response.brands
    
    // Sync with Layout component's brand selection from localStorage
    const savedBrandId = localStorage.getItem('selectedBrandId')
    if (savedBrandId) {
      selectedBrandId.value = savedBrandId
    }
  } catch (error) {
    console.error('Failed to load brands:', error)
  }
  
  // Listen for brand changes from Layout component
  window.addEventListener('brand-project-changed', ((event: CustomEvent) => {
    if (event.detail?.brandId) {
      selectedBrandId.value = event.detail.brandId
    } else {
      selectedBrandId.value = null
    }
  }) as EventListener)
  
  // Don't start conversation automatically - show task selection
})

// Auto-scroll to bottom when new messages arrive
watch(messages, () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}, { deep: true })

// Helper functions
function addMessage(role: 'user' | 'bot', text: string) {
  messages.value.push({
    role,
    text,
    timestamp: new Date(),
  })
}

/**
 * Filter and categorize colors to get only the best examples of each color type
 * Returns 6-12 colors max, categorized as Primary, Secondary, Accent, Light, Dark, Neutral
 */
function filterAndCategorizeColors(colors: any[]): Array<{ hex: string; name?: string }> {
  if (!colors || colors.length === 0) return []

  // Convert colors to a format with HSL for categorization
  const colorsWithHSL = colors.map((c: any) => {
    let hex = c.hex
    if (!hex && c.rgb) {
      hex = `#${[c.rgb.r, c.rgb.g, c.rgb.b].map((x: number) => x.toString(16).padStart(2, '0')).join('')}`
    }
    
    // Calculate HSL if not provided
    let hsl = c.hsl
    if (!hsl && c.rgb) {
      hsl = rgbToHsl(c.rgb.r, c.rgb.g, c.rgb.b)
    }
    
    return {
      hex,
      rgb: c.rgb,
      hsl,
      percentage: c.percentage || 0,
    }
  })

  // Helper to convert RGB to HSL
  function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
    r /= 255
    g /= 255
    b /= 255
    
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h = 0
    let s = 0
    const l = (max + min) / 2
    
    if (max !== min) {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
        case g: h = ((b - r) / d + 2) / 6; break
        case b: h = ((r - g) / d + 4) / 6; break
      }
    }
    
    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    }
  }

  // Categorize colors
  const categories: Record<string, any[]> = {
    primary: [],
    secondary: [],
    accent: [],
    light: [],
    dark: [],
    neutral: [],
  }

  // Sort by percentage (dominance) first
  const sortedByPercentage = [...colorsWithHSL].sort((a, b) => (b.percentage || 0) - (a.percentage || 0))

  sortedByPercentage.forEach((color, index) => {
    const { s, l } = color.hsl || { s: 0, l: 50 }
    
    // Neutral: low saturation (< 20%) - grays, beiges, etc.
    if (s < 20) {
      categories.neutral.push(color)
    }
    // Light: high lightness (> 80%) - pastels, whites, light colors
    else if (l > 80) {
      categories.light.push(color)
    }
    // Dark: low lightness (< 30%) - dark colors, blacks, deep colors
    else if (l < 30) {
      categories.dark.push(color)
    }
    // Accent: high saturation (> 60%) and medium-high lightness (40-70%) - vibrant colors
    else if (s > 60 && l >= 40 && l <= 70) {
      categories.accent.push(color)
    }
    // Primary/Secondary: colors with good saturation (> 30%)
    // Top 2 most dominant become Primary, others become Secondary
    else if (s > 30) {
      if (index < 2) {
        categories.primary.push(color)
      } else {
        categories.secondary.push(color)
      }
    }
    // Fallback: if color doesn't fit any category, add to secondary
    else {
      categories.secondary.push(color)
    }
  })

  // Select best examples from each category
  const selectedColors: Array<{ hex: string; name?: string }> = []
  
  // Primary: top 1-2 colors (most dominant)
  if (categories.primary.length > 0) {
    const primary = categories.primary
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .slice(0, 2)
    primary.forEach((c, i) => {
      selectedColors.push({ hex: c.hex, name: i === 0 ? 'Primary' : undefined })
    })
  }

  // Secondary: next 1-2 best colors
  if (categories.secondary.length > 0 && selectedColors.length < 10) {
    const secondary = categories.secondary
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .slice(0, 2)
    secondary.forEach((c, i) => {
      if (selectedColors.length < 12) {
        selectedColors.push({ hex: c.hex, name: i === 0 ? 'Secondary' : undefined })
      }
    })
  }

  // Accent: best 1-2 accent colors
  if (categories.accent.length > 0 && selectedColors.length < 10) {
    const accent = categories.accent
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .slice(0, 2)
    accent.forEach((c) => {
      if (selectedColors.length < 12) {
        selectedColors.push({ hex: c.hex, name: 'Accent' })
      }
    })
  }

  // Light: best 1-2 light colors
  if (categories.light.length > 0 && selectedColors.length < 10) {
    const light = categories.light
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .slice(0, 2)
    light.forEach((c) => {
      if (selectedColors.length < 12) {
        selectedColors.push({ hex: c.hex, name: 'Light' })
      }
    })
  }

  // Dark: best 1-2 dark colors
  if (categories.dark.length > 0 && selectedColors.length < 10) {
    const dark = categories.dark
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .slice(0, 2)
    dark.forEach((c) => {
      if (selectedColors.length < 12) {
        selectedColors.push({ hex: c.hex, name: 'Dark' })
      }
    })
  }

  // Neutral: best 1-2 neutral colors
  if (categories.neutral.length > 0 && selectedColors.length < 10) {
    const neutral = categories.neutral
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .slice(0, 2)
    neutral.forEach((c) => {
      if (selectedColors.length < 12) {
        selectedColors.push({ hex: c.hex, name: 'Neutral' })
      }
    })
  }

  // If we still have room and colors, fill with remaining top colors
  if (selectedColors.length < 12) {
    const remaining = sortedByPercentage.filter(
      (c) => !selectedColors.some((sc) => sc.hex === c.hex)
    )
    remaining.slice(0, 12 - selectedColors.length).forEach((c) => {
      selectedColors.push({ hex: c.hex })
    })
  }

  // Limit to 12 colors max
  return selectedColors.slice(0, 12)
}

function addBotMessage(text: string) {
  addMessage('bot', text)
}

function addUserMessage(text: string) {
  addMessage('user', text)
}

function formatMessage(text: string): string {
  // Simple formatting - convert newlines to <br>
  return text.replace(/\n/g, '<br>')
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// Helper functions for mini brief labels
function getProjectTypeLabel(type: 'photo_post' | 'video' | 'toolkit' | null): string {
  if (!type) return ''
  const labels: Record<string, string> = {
    photo_post: 'Photo post',
    video: 'Video',
    toolkit: 'Toolkit',
  }
  return labels[type] || ''
}

function getAspectRatioLabel(ratio: 'square' | '4x5' | 'multiple' | null): string {
  if (!ratio) return ''
  const labels: Record<string, string> = {
    square: 'Square',
    '4x5': '4x5',
    multiple: 'Multiple Variations',
  }
  return labels[ratio] || ''
}

// Validate slide input (supports single number or range like "5-7" or "5 to 7")
function isValidSlideInput(input: string): boolean {
  if (!input || !input.trim()) return false
  
  const trimmed = input.trim()
  
  // Check for single number
  const singleNumber = /^\d+$/.test(trimmed)
  if (singleNumber) {
    const num = parseInt(trimmed, 10)
    return num >= 1
  }
  
  // Check for range with hyphen (e.g., "5-7", "5 - 7")
  const hyphenRange = /^(\d+)\s*-\s*(\d+)$/.exec(trimmed)
  if (hyphenRange) {
    const min = parseInt(hyphenRange[1], 10)
    const max = parseInt(hyphenRange[2], 10)
    return min >= 1 && max >= 1 && min <= max
  }
  
  // Check for range with "to" (e.g., "5 to 7", "5to7")
  const toRange = /^(\d+)\s+to\s+(\d+)$/i.exec(trimmed)
  if (toRange) {
    const min = parseInt(toRange[1], 10)
    const max = parseInt(toRange[2], 10)
    return min >= 1 && max >= 1 && min <= max
  }
  
  return false
}

// Format slide input for display (normalizes ranges)
function formatSlideInput(input: string): string {
  if (!input || !input.trim()) return ''
  
  const trimmed = input.trim()
  
  // Single number - return as is
  if (/^\d+$/.test(trimmed)) {
    return trimmed
  }
  
  // Range with hyphen - normalize to "5-7"
  const hyphenRange = /^(\d+)\s*-\s*(\d+)$/.exec(trimmed)
  if (hyphenRange) {
    return `${hyphenRange[1]}-${hyphenRange[2]}`
  }
  
  // Range with "to" - convert to hyphen format
  const toRange = /^(\d+)\s+to\s+(\d+)$/i.exec(trimmed)
  if (toRange) {
    return `${toRange[1]}-${toRange[2]}`
  }
  
  return trimmed
}

// Conversation flow handlers
async function submitBrandName() {
  if (!brandNameInput.value.trim()) {
    message.warning('Please enter a brand name')
    return
  }

  const brandName = brandNameInput.value.trim()
  addUserMessage(brandName)
  discoveredInfo.value.brandName = brandName // Store brand name for confirmation form
  brandNameInput.value = ''
  
  addBotMessage(`Great! Now let's find your website. What's your website URL?`)
  currentStep.value = 'collect_website'
}

async function submitWebsite() {
  if (!websiteInput.value.trim()) {
    message.warning('Please enter a website URL')
    return
  }

  const website = websiteInput.value.trim()
  addUserMessage(website)
  const savedWebsite = website // Store for later use
  websiteInput.value = ''
  
  // Store website for later use
  websiteContent.value = savedWebsite
  
  // Start search immediately (this will find Instagram too)
  await startSearch()
}

async function startSearch() {
  isLoading.value = true
  currentStep.value = 'searching'
  progressStep.value = 1
  progressStatus.value = 'process'

  addBotMessage('Let me search for your brand information... This might take a moment.')

  try {
    const website = messages.value.find(m => m.role === 'user' && (m.text.includes('http') || m.text.includes('www.')))?.text || ''

    const searchResults = await apiClient.searchBrandInfo({
      website: website || undefined,
    })

    // Store discovered info (preserve brandName)
    discoveredInfo.value = {
      brandName: discoveredInfo.value.brandName || '',
      address: searchResults.address || '',
      phone: searchResults.phone || '',
      email: searchResults.email || '',
      instagramHandle: searchResults.instagramHandle || discoveredInfo.value.instagramHandle || '',
      socialLinks: searchResults.socialLinks || [],
      logoUrls: searchResults.logoUrls || [],
      photoUrls: searchResults.photoUrls || [],
      colorPalette: searchResults.colorPalette || null,
    }

    // Store website content for analysis
    if (searchResults.websiteContent) {
      websiteContent.value = searchResults.websiteContent
    }

    // Store discovered Instagram handle if found
    if (searchResults.instagramHandle) {
      discoveredInfo.value.instagramHandle = searchResults.instagramHandle
    }

    // Show discovered info
    let infoText = 'I found some information about your brand:\n\n'
    if (discoveredInfo.value.address) infoText += `📍 Address: ${discoveredInfo.value.address}\n`
    if (discoveredInfo.value.phone) infoText += `📞 Phone: ${discoveredInfo.value.phone}\n`
    if (discoveredInfo.value.email) infoText += `📧 Email: ${discoveredInfo.value.email}\n`
    if (discoveredInfo.value.instagramHandle) infoText += `📷 Instagram: @${discoveredInfo.value.instagramHandle}\n`
    if (discoveredInfo.value.logoUrls.length > 0) infoText += `🖼️ Found ${discoveredInfo.value.logoUrls.length} logo(s)\n`
    if (discoveredInfo.value.photoUrls.length > 0) infoText += `📸 Found ${discoveredInfo.value.photoUrls.length} photo(s)\n`
    if (discoveredInfo.value.colorPalette) infoText += `🎨 Extracted color palette\n`

    addBotMessage(infoText)
    addBotMessage('Please review and confirm the information above to continue to analysis.')

    currentStep.value = 'confirm_info'
    progressStep.value = 2
    progressStatus.value = 'finish'
  } catch (error) {
    message.error('Failed to search for brand information')
    addBotMessage('I had trouble finding information. Let\'s continue with analysis anyway.')
    await startAnalysis()
  } finally {
    isLoading.value = false
  }

}

// Remove image from being added to assets
function removeImage(url: string, type: 'logo' | 'photo') {
  removedImageUrls.value.add(url)
  message.info(`${type === 'logo' ? 'Logo' : 'Photo'} removed. It will not be added to assets.`)
}

async function confirmInfo() {
  isLoading.value = true
  await startAnalysis()
}

async function startAnalysis() {
  currentStep.value = 'analyzing'
  progressStep.value = 2
  progressStatus.value = 'process'

  addBotMessage('Now let me analyze your website and generate brand information using AI... This will take a moment.')

  try {
    const websiteUrl = messages.value.find(m => m.role === 'user' && (m.text.includes('http') || m.text.includes('www.')))?.text || ''
    
    if (!websiteUrl) {
      throw new Error('Website URL is required for analysis')
    }

    // Get website metadata if we have it from the search
    const websiteMetadata = websiteContent.value ? {
      // We could extract title/description from the stored content if needed
      // For now, just pass the URL
    } : undefined
    
    // Use discovered Instagram handle if available
    const instagramHandle = discoveredInfo.value.instagramHandle || undefined
    
    const brandName = messages.value.find(m => m.role === 'user' && !m.text.includes('http') && !m.text.includes('www.'))?.text || brandNameInput.value || undefined
    
    const analysis = await apiClient.analyzeBrandContent({
      websiteUrl,
      brandName,
      instagramHandle,
      websiteMetadata,
    })

    // Store generated content
    generatedContent.value = {
      brandCode: analysis.brandCode || '',
      elevatorPitch: analysis.elevatorPitch,
      voiceTone: analysis.voiceTone,
      taglines: analysis.taglines || [],
      positiveKeywords: analysis.positiveKeywords || [],
      negativeKeywords: analysis.negativeKeywords || [],
      colorPalette: analysis.colorPalette || null,
      logoUrls: analysis.logoUrls || [],
    }

    // Merge discovered info (excluding removed images)
    if (discoveredInfo.value.logoUrls.length > 0 && generatedContent.value.logoUrls.length === 0) {
      generatedContent.value.logoUrls = discoveredInfo.value.logoUrls.filter(url => !removedImageUrls.value.has(url))
    }
    if (discoveredInfo.value.colorPalette && !generatedContent.value.colorPalette) {
      generatedContent.value.colorPalette = discoveredInfo.value.colorPalette
    }

    addBotMessage('Great! I\'ve analyzed your brand and generated some placeholder brand direction and keywords. Please review and edit as needed, then save to your brand.')

    currentStep.value = 'confirm_generated'
    progressStep.value = 3
    progressStatus.value = 'finish'
  } catch (error) {
    message.error('Failed to analyze brand content')
    addBotMessage('I had trouble analyzing your brand. Please try again or fill in the information manually.')
    progressStatus.value = 'error'
  } finally {
    isLoading.value = false
  }
}

async function saveBrand() {
  if (!generatedContent.value.brandCode) {
    message.warning('Brand code is required. Please wait for analysis to complete.')
    return
  }

  isSaving.value = true

  try {
    const websiteUrl = messages.value.find(m => m.role === 'user' && (m.text.includes('http') || m.text.includes('www.')))?.text || undefined
    
    const brandName = discoveredInfo.value.brandName || messages.value.find(m => m.role === 'user' && !m.text.includes('http') && !m.text.includes('www.'))?.text || 'New Brand'
    
    // Prepare brand data
    const brandData: any = {
      name: brandName,
      code: generatedContent.value.brandCode, // Use brandCode as the code
      brandCode: generatedContent.value.brandCode,
      address: discoveredInfo.value.address || undefined,
      phone: discoveredInfo.value.phone || undefined,
      contactEmail: discoveredInfo.value.email || undefined,
      website: websiteUrl,
      elevatorPitch: generatedContent.value.elevatorPitch || undefined,
      voiceTone: generatedContent.value.voiceTone || undefined,
      taglines: generatedContent.value.taglines.length > 0 ? generatedContent.value.taglines : undefined,
      positiveBrandKeywords: generatedContent.value.positiveKeywords.length > 0 ? generatedContent.value.positiveKeywords : undefined,
      negativeBrandKeywords: generatedContent.value.negativeKeywords.length > 0 ? generatedContent.value.negativeKeywords : undefined,
    }

    // Add logos if found (excluding removed ones)
    const allLogos = [...(generatedContent.value.logoUrls || []), ...(discoveredInfo.value.logoUrls || [])]
      .filter(url => !removedImageUrls.value.has(url))
    if (allLogos.length > 0) {
      brandData.logoUrls = [...new Set(allLogos)]
    }

    // Add colors if found - filter to best examples of each color type (6-12 colors max)
    if (generatedContent.value.colorPalette || discoveredInfo.value.colorPalette) {
      const palette = generatedContent.value.colorPalette || discoveredInfo.value.colorPalette
      if (palette && palette.colors) {
        // Filter and categorize colors to get only the best examples
        const filteredColors = filterAndCategorizeColors(palette.colors)
        
        if (filteredColors.length > 0) {
          brandData.colors = [{
            name: 'Website Colors',
            colors: filteredColors,
          }]
        }
      }
    }

    // Add Instagram handle if provided (from discovered info)
    if (discoveredInfo.value.instagramHandle) {
      brandData.igHandle = discoveredInfo.value.instagramHandle.replace('@', '').trim()
    }

    // Check if brand already exists by code
    let brandResponse
    let newBrandId: string
    let isUpdate = false
    
    try {
      // Try to get existing brands to check if code exists
      const existingBrands = await apiClient.getBrands({ limit: 1000 })
      const existingBrand = existingBrands.brands.find((b: any) => b.code === brandData.code)
      
      if (existingBrand) {
        // Brand exists, update it instead
        isUpdate = true
        addBotMessage(`Brand with code "${brandData.code}" already exists. Updating existing brand...`)
        brandResponse = await apiClient.updateBrand(existingBrand.id, brandData)
        newBrandId = existingBrand.id
        message.success(`Brand "${brandName}" updated successfully!`)
      } else {
        // Brand doesn't exist, create new one
        brandResponse = await apiClient.createBrand(brandData)
        newBrandId = brandResponse.brand.id
        message.success('Brand created successfully!')
      }
    } catch (createError: any) {
      // If create fails with "already exists" error, try to find and update
      if (createError.response?.data?.error?.includes('already exists') || 
          createError.response?.data?.message?.includes('already exists')) {
        try {
          const existingBrands = await apiClient.getBrands({ limit: 1000 })
          const existingBrand = existingBrands.brands.find((b: any) => b.code === brandData.code)
          
          if (existingBrand) {
            isUpdate = true
            addBotMessage(`Brand with code "${brandData.code}" already exists. Updating existing brand...`)
            brandResponse = await apiClient.updateBrand(existingBrand.id, brandData)
            newBrandId = existingBrand.id
            message.success(`Brand "${brandName}" updated successfully!`)
          } else {
            throw createError // Re-throw if we can't find the brand
          }
        } catch (updateError) {
          // If update also fails, show clear error
          throw new Error(`Brand with code "${brandData.code}" already exists, but I couldn't update it. Please use a different brand code or update the existing brand manually.`)
        }
      } else {
        // Re-throw other errors
        throw createError
      }
    }

    // Create a 'reference' gallery for this brand (private gallery for website images)
    let referenceGalleryId: string | null = null

    // Create a 'scratch' project for this brand (only if it's a new brand)
    if (!isUpdate) {
      try {
        await apiClient.createProject({
          name: 'scratch',
          code: 'scratch',
          brandId: newBrandId,
          description: 'Scratch project for initial work',
        })
      } catch (projectError: any) {
        // If project already exists, that's fine - just log it
        if (projectError.response?.data?.error?.includes('already exists')) {
          console.log('Scratch project already exists for this brand')
        } else {
          // Log but don't fail if project creation fails for other reasons
          console.warn('Failed to create scratch project:', projectError)
        }
      }

      // Create a 'reference' gallery for this brand (private gallery for website images)
      try {
        const referenceGallery = await apiClient.createGallery({
          name: 'reference',
          description: 'Reference images from website discovery',
          ownerBrandId: newBrandId,
          isPrivate: true,
        })
        referenceGalleryId = referenceGallery.id
        addBotMessage('Created reference gallery for brand images')
      } catch (galleryError: any) {
        // If gallery already exists, try to find it
        console.warn('Failed to create reference gallery:', galleryError)
        // Don't fail the whole operation if gallery creation fails
      }
    }

    // Upload photos as RefImages if we found any (excluding removed ones)
    const allPhotoUrls = (discoveredInfo.value.photoUrls || []).filter(url => !removedImageUrls.value.has(url))
    if (allPhotoUrls.length > 0) {
      try {
        addBotMessage(`Uploading ${allPhotoUrls.length} photos to the gallery...`)
        
        // Get or find the reference gallery if we're updating an existing brand
        if (isUpdate && !referenceGalleryId) {
          try {
            // Try to find existing reference gallery for this brand
            const galleries = await apiClient.getGalleries({ brandId: newBrandId })
            const referenceGallery = galleries.galleries?.find((g: any) => g.name === 'reference' && g.ownerBrandId === newBrandId)
            if (referenceGallery) {
              referenceGalleryId = referenceGallery.id
            } else {
              // Create it if it doesn't exist
              const newGallery = await apiClient.createGallery({
                name: 'reference',
                description: 'Reference images from website discovery',
                ownerBrandId: newBrandId,
                isPrivate: true,
              })
              referenceGalleryId = newGallery.id
            }
          } catch (galleryError) {
            console.warn('Failed to get/create reference gallery:', galleryError)
          }
        }
        
        const uploadResult = await apiClient.uploadBrandPhotos({
          photoUrls: allPhotoUrls,
          brandId: newBrandId,
          galleryId: referenceGalleryId || undefined,
        })
        if (uploadResult.uploaded > 0) {
          addBotMessage(`✅ Successfully uploaded ${uploadResult.uploaded} photo(s) to the gallery!`)
        }
        if (uploadResult.failed > 0) {
          addBotMessage(`⚠️ ${uploadResult.failed} photo(s) failed to upload`)
        }
      } catch (photoError) {
        console.warn('Failed to upload brand photos:', photoError)
        // Don't fail the whole operation if photo upload fails
        addBotMessage('⚠️ Some photos could not be uploaded, but the brand was created successfully.')
      }
    }

    // Update selected brand and refresh list
    selectedBrandId.value = newBrandId
    const response = await apiClient.getBrands({ limit: 100 })
    brands.value = response.brands

    if (isUpdate) {
      addBotMessage(`Perfect! I've updated your brand "${brandName}" (${generatedContent.value.brandCode}). You can view it in the Brand tab.`)
    } else {
      addBotMessage(`Perfect! I've created your brand "${brandName}" (${generatedContent.value.brandCode}) and a "scratch" project. You can view them in the Brand and Project tabs.`)
    }

    currentStep.value = 'completed'
    progressStep.value = 4
    progressStatus.value = 'finish'
  } catch (error: any) {
    console.error('Failed to save brand:', error)
    
    // Show clearer error messages
    let errorMessage = 'Failed to save brand information'
    let botMessage = 'I had trouble saving the brand. Please try again.'
    
    if (error.response?.data?.error) {
      const apiError = error.response.data.error
      if (typeof apiError === 'string') {
        if (apiError.includes('already exists')) {
          errorMessage = `Brand code "${generatedContent.value.brandCode}" is already in use`
          botMessage = `A brand with code "${generatedContent.value.brandCode}" already exists. Please choose a different brand code or update the existing brand manually.`
        } else {
          errorMessage = apiError
          botMessage = `Error: ${apiError}`
        }
      } else if (apiError.message) {
        errorMessage = apiError.message
        botMessage = `Error: ${apiError.message}`
      }
    } else if (error.message) {
      errorMessage = error.message
      botMessage = error.message
    }
    
    message.error(errorMessage)
    addBotMessage(botMessage)
    progressStatus.value = 'error'
  } finally {
    isSaving.value = false
  }
}

async function sendMessage() {
  if (!inputMessage.value.trim() || isLoading.value) return

  const text = inputMessage.value.trim()
  addUserMessage(text)
  inputMessage.value = ''

  // Simple message handling - can be expanded
  isLoading.value = true
  try {
    const response = await apiClient.chatBrandDiscovery(text, selectedBrandId.value || undefined)
    if (response.response) {
      addBotMessage(response.response)
    }
    if (response.nextStep) {
      currentStep.value = response.nextStep as any
    }
  } catch (error) {
    message.error('Failed to send message')
  } finally {
    isLoading.value = false
  }
}

function restart() {
  // Reset to task selection
  currentTask.value = null
  messages.value = []
  currentStep.value = 'greeting'
  progressStep.value = 0
  progressStatus.value = 'process'
  
  // Reset brand setup state
  websiteInput.value = ''
  instagramInput.value = ''
  removedImageUrls.value.clear()
  discoveredInfo.value = {
    brandName: '',
    address: '',
    phone: '',
    email: '',
    instagramHandle: '',
    socialLinks: [],
    logoUrls: [],
    photoUrls: [],
    colorPalette: null,
  }
  brandNameInput.value = ''
  websiteInput.value = ''
  generatedContent.value = {
    brandCode: '',
    elevatorPitch: '',
    voiceTone: '',
    taglines: [],
    positiveKeywords: [],
    negativeKeywords: [],
    colorPalette: null,
    logoUrls: [],
  }
  websiteContent.value = ''
  
  // Reset new project state
  projectType.value = null
  photoAspectRatio.value = null
  photoSlides.value = ''
  photoContent.value = ''
  photoAssetsNeeded.value = null
  photoAssetsSource.value = null
  deliverablesCount.value = null
  uploadedFiles.value = []
  projectName.value = ''
  projectCode.value = ''
  projectDescription.value = ''
}


// New Project flow handlers
function selectProjectType(type: 'photo_post' | 'video' | 'toolkit') {
  projectType.value = type
  
  const typeLabels: Record<string, string> = {
    photo_post: 'Photo Post',
    video: 'Video',
    toolkit: 'Toolkit',
  }
  
  addUserMessage(typeLabels[type])
  
  if (type === 'photo_post') {
    addBotMessage(`Great! What aspect ratio do you need? Square, 4x5, or multiple variations?`)
    currentStep.value = 'photo_aspect_ratio'
    progressStep.value = 1 // Step 1 (Project Type) is complete, now on step 2 (Aspect Ratio) - index 1 becomes step 2 in UI
  } else {
    // For video and toolkit, go to deliverables
    addBotMessage(`Got it! How many deliverables or variations do you need for this project?`)
    currentStep.value = 'deliverables'
    progressStep.value = 1 // Step 1 (Project Type) is complete, now on step 2 (Deliverables) - index 1 becomes step 2 in UI
  }
}

function selectAspectRatio(ratio: 'square' | '4x5' | 'multiple') {
  photoAspectRatio.value = ratio
  
  const ratioLabels: Record<string, string> = {
    square: 'Square',
    '4x5': '4x5',
    multiple: 'Multiple Variations',
  }
  
  addUserMessage(ratioLabels[ratio])
  addBotMessage(`Perfect! How many slides do you roughly think you'll need?`)
  currentStep.value = 'photo_slides'
  progressStep.value = 2 // Step 2 (Aspect Ratio) is complete, now on step 3 (Slides) - index 2 becomes step 3 in UI
}

function submitPhotoSlides() {
  if (!isValidSlideInput(photoSlides.value)) {
    message.warning('Please enter a valid number or range of slides (e.g., 5-7 or 5 to 7)')
    return
  }
  
  // Format the input for display
  const formattedSlides = formatSlideInput(photoSlides.value)
  photoSlides.value = formattedSlides // Normalize the value
  
  // Display in message
  const slideText = formattedSlides.includes('-') 
    ? `${formattedSlides} slides`
    : `${formattedSlides} slide${formattedSlides !== '1' ? 's' : ''}`
  
  addUserMessage(slideText)
  addBotMessage(`Got it! Tell us about the content of the post. What's it about?`)
  currentStep.value = 'photo_content'
  progressStep.value = 3 // Step 3 (Slides) is complete, now on step 4 (Content) - index 3 becomes step 4 in UI
}

function submitPhotoContent() {
  if (!photoContent.value.trim()) {
    message.warning('Please describe the content of the post')
    return
  }
  
  addUserMessage(photoContent.value)
  addBotMessage(`Thanks! Are there additional assets that we need, or does everything already exist in the toolkit?`)
  currentStep.value = 'photo_assets_needed'
  progressStep.value = 4 // Step 4 (Content) is complete, now on step 5 (Assets) - index 4 becomes step 5 in UI
}

function selectAssetsNeeded(needed: boolean) {
  photoAssetsNeeded.value = needed
  
  if (needed) {
    addUserMessage('Need New Assets')
    addBotMessage(`Do you have assets to upload, or are we creating new ones?`)
    currentStep.value = 'photo_assets_source'
    progressStep.value = 4 // Still on step 5 (Assets), just a sub-question - index 4 becomes step 5 in UI
  } else {
    addUserMessage('Everything Exists')
    // Skip to asset upload step (which will be skipped) or go to confirm
    proceedToPhotoConfirm()
  }
}

function selectAssetsSource(source: 'upload' | 'create') {
  photoAssetsSource.value = source
  
  if (source === 'upload') {
    addUserMessage('I Have Assets to Upload')
    addBotMessage(`Perfect! Let's upload your assets.`)
    currentStep.value = 'asset_upload'
    progressStep.value = 4 // Still on step 5 (Assets) - index 4 becomes step 5 in UI
  } else {
    addUserMessage('Create New Assets')
    addBotMessage(`Got it! We'll create new assets for this project.`)
    proceedToPhotoConfirm()
  }
}

function generateProjectName(): string {
  if (projectType.value === 'photo_post') {
    const aspectRatio = photoAspectRatio.value === 'square' ? 'Square' : 
                        photoAspectRatio.value === '4x5' ? '4x5' : 
                        'Multi-Aspect'
    let slidesText = ''
    if (photoSlides.value) {
      if (photoSlides.value.includes('-')) {
        slidesText = `${photoSlides.value} Slides`
      } else {
        slidesText = `${photoSlides.value} Slide${photoSlides.value !== '1' ? 's' : ''}`
      }
    }
    return `${aspectRatio} Photo Post${slidesText ? ` - ${slidesText}` : ''}`
  } else if (projectType.value === 'video') {
    return `Video Project${deliverablesCount.value ? ` - ${deliverablesCount.value} Deliverable${deliverablesCount.value > 1 ? 's' : ''}` : ''}`
  } else if (projectType.value === 'toolkit') {
    return `Toolkit Project${deliverablesCount.value ? ` - ${deliverablesCount.value} Asset${deliverablesCount.value > 1 ? 's' : ''}` : ''}`
  }
  return 'New Project'
}

function generateProjectCode(): string {
  // Generate a 4-12 character alphanumeric code
  const typePrefix = projectType.value === 'photo_post' ? 'photo' : 
                     projectType.value === 'video' ? 'vid' : 
                     projectType.value === 'toolkit' ? 'tool' : 'proj'
  
  // Get current date for uniqueness (YYMMDD)
  const now = new Date()
  const dateStr = `${now.getFullYear().toString().slice(-2)}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}`
  
  // Combine: prefix + date = 4-12 chars
  const code = `${typePrefix}${dateStr}`.slice(0, 12)
  return code
}

function generateProjectDescription(): string {
  const parts: string[] = []
  
  if (projectType.value === 'photo_post') {
    parts.push(`Photo Post Project`)
    
    if (photoAspectRatio.value) {
      const ratioText = photoAspectRatio.value === 'square' ? 'Square (1:1)' :
                       photoAspectRatio.value === '4x5' ? '4x5 (Portrait)' :
                       'Multiple Aspect Ratios'
      parts.push(`Aspect Ratio: ${ratioText}`)
    }
    
    if (photoSlides.value) {
      const slidesText = photoSlides.value.includes('-') 
        ? `${photoSlides.value} slides`
        : `${photoSlides.value} slide${photoSlides.value !== '1' ? 's' : ''}`
      parts.push(`Number of slides: ${slidesText}`)
    }
    
    if (photoContent.value) {
      parts.push(`Content: ${photoContent.value}`)
    }
    
    if (photoAssetsNeeded.value !== null) {
      if (photoAssetsNeeded.value === false) {
        parts.push(`Assets: Using existing toolkit assets`)
      } else {
        if (photoAssetsSource.value === 'upload') {
          parts.push(`Assets: User will upload assets`)
        } else if (photoAssetsSource.value === 'create') {
          parts.push(`Assets: New assets will be created`)
        }
      }
    }
  } else if (projectType.value === 'video') {
    parts.push(`Video Project`)
    if (deliverablesCount.value) {
      parts.push(`Deliverables: ${deliverablesCount.value} variation${deliverablesCount.value > 1 ? 's' : ''}`)
    }
  } else if (projectType.value === 'toolkit') {
    parts.push(`Toolkit Project`)
    if (deliverablesCount.value) {
      parts.push(`Assets: ${deliverablesCount.value} asset${deliverablesCount.value > 1 ? 's' : ''}`)
    }
  }
  
  return parts.join('\n\n')
}

function proceedToPhotoConfirm() {
  // Auto-generate project name, code, and description
  if (!projectName.value) {
    projectName.value = generateProjectName()
  }
  if (!projectCode.value) {
    projectCode.value = generateProjectCode()
  }
  if (!projectDescription.value) {
    projectDescription.value = generateProjectDescription()
  }
  
  currentStep.value = 'confirm_create'
  progressStep.value = 5 // Step 5 (Assets) is complete, now on step 6 (Create) - index 5 becomes step 6 in UI
}

function submitDeliverables() {
  if (!deliverablesCount.value || deliverablesCount.value < 1) {
    message.warning('Please enter a valid number of deliverables')
    return
  }
  
  addUserMessage(`${deliverablesCount.value} deliverable${deliverablesCount.value > 1 ? 's' : ''}`)
  addBotMessage(`Perfect! Now let's upload or import any assets you already have. You can skip this step and add assets later if needed.`)
  currentStep.value = 'asset_upload'
  progressStep.value = 2 // Step 3 (Deliverables) is complete, now on step 4 (Assets) - index 2 becomes step 3 in UI (for video/toolkit)
}

function proceedToConfirm() {
  // Auto-generate project name, code, and description
  if (!projectName.value) {
    projectName.value = generateProjectName()
  }
  if (!projectCode.value) {
    projectCode.value = generateProjectCode()
  }
  if (!projectDescription.value) {
    projectDescription.value = generateProjectDescription()
  }
  
  currentStep.value = 'confirm_create'
  // Set progress step based on project type
  if (projectType.value === 'photo_post') {
    progressStep.value = 5 // Step 5 (Assets) is complete, now on step 6 (Create) - index 5 becomes step 6 in UI
  } else {
    progressStep.value = 3 // Step 3 (Assets) is complete, now on step 4 (Create) - index 3 becomes step 4 in UI
  }
}

function skipAssetUpload() {
  addBotMessage(`No problem! You can add assets later. Let's finalize the project details.`)
  if (projectType.value === 'photo_post') {
    proceedToPhotoConfirm()
  } else {
    proceedToConfirm()
  }
}

async function submitAssetUpload() {
  if (uploadedFiles.value.length === 0) {
    skipAssetUpload()
    return
  }
  
  isLoading.value = true
  try {
    addBotMessage(`Uploading ${uploadedFiles.value.length} file(s)...`)
    // TODO: Implement actual file upload to project
    // For now, just proceed to confirmation
    await new Promise(resolve => setTimeout(resolve, 500)) // Simulate upload
    addBotMessage(`Great! ${uploadedFiles.value.length} file(s) uploaded. Now let's finalize the project details.`)
    if (projectType.value === 'photo_post') {
      proceedToPhotoConfirm()
    } else {
      proceedToConfirm()
    }
  } catch (error) {
    message.error('Failed to upload files')
    addBotMessage('I had trouble uploading some files, but we can continue. You can add them later.')
    if (projectType.value === 'photo_post') {
      proceedToPhotoConfirm()
    } else {
      proceedToConfirm()
    }
  } finally {
    isLoading.value = false
  }
}

function goBackToAssetUpload() {
  currentStep.value = 'asset_upload'
  if (projectType.value === 'photo_post') {
    progressStep.value = 3 // Back to step 5 (Assets)
  } else {
    progressStep.value = 2 // Back to step 3 (Assets)
  }
}

async function createProject() {
  if (!projectName.value || !projectCode.value) {
    message.warning('Please fill in project name and code')
    return
  }
  
  if (!selectedBrandId.value) {
    message.warning('Please select a brand')
    return
  }
  
  isSaving.value = true
  
  try {
    addBotMessage(`Creating your project "${projectName.value}"...`)
    
    await apiClient.createProject({
      name: projectName.value,
      code: projectCode.value,
      brandId: selectedBrandId.value,
      description: projectDescription.value || undefined,
    })
    
    message.success('Project created successfully!')
    addBotMessage(`Perfect! I've created your project "${projectName.value}" with all the default assets. You can view it in the Projects tab.`)
    
    currentStep.value = 'completed'
    if (projectType.value === 'photo_post') {
      progressStep.value = 5 // All 6 steps complete (0-5)
    } else {
      progressStep.value = 3 // All 4 steps complete (0-3)
    }
    progressStatus.value = 'finish'
  } catch (error: any) {
    console.error('Failed to create project:', error)
    message.error(error.response?.data?.message || 'Failed to create project')
    addBotMessage('I had trouble creating the project. Please try again.')
    progressStatus.value = 'error'
  } finally {
    isSaving.value = false
  }
}
</script>

<style scoped>
.chat-tab {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--base-color);
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: 24px;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 20px;
  background: var(--card-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.header-content {
  flex: 1;
}

.chat-title {
  margin: 0 0 4px 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--text-color);
}

.chat-subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--text-color-2);
}

.main-content {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 24px;
  flex: 1;
  min-height: 0;
}

.chat-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: var(--card-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  margin-bottom: 16px;
  min-height: 300px;
}

.mini-brief-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.mini-brief-card {
  height: fit-content;
  position: sticky;
  top: 24px;
  background: var(--card-color);
}

.mini-brief-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.brief-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.brief-label {
  font-size: 12px;
  color: var(--text-color-2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.brief-value {
  font-size: 14px;
  color: var(--text-color);
  word-wrap: break-word;
}

.brief-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  min-height: 200px;
}

.brief-empty-text {
  font-size: 14px;
  color: var(--text-color-2);
  text-align: center;
}

.message {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
  align-items: flex-start;
}

.message.user {
  flex-direction: row-reverse;
}

.message-avatar-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.message.user .message-avatar-wrapper {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message.user .message-avatar {
  background: var(--text-color-2);
}

.message-separator {
  border: none;
  border-top: 1px solid var(--border-color);
  margin: 8px 0;
  opacity: 0.3;
}

.message-content {
  flex: 1;
  max-width: 70%;
  margin-top: -4px;
}

.message.user .message-content {
  text-align: right;
}

.message-text {
  padding: 10px 14px;
  background: var(--base-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  color: var(--text-color);
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.5;
}

.message.user .message-text {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.message-time {
  font-size: 11px;
  color: var(--text-color-2);
  white-space: nowrap;
}

.chat-input-area {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.structured-input,
.chat-input {
  width: 100%;
}

.confirmation-panel {
  width: 100%;
}

.confirmation-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 16px;
}

.progress-indicator {
  padding: 16px;
  background: var(--card-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.task-selection {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
}

.task-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 24px;
}

.task-option {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.task-option:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.task-option--disabled:hover {
  transform: none;
  box-shadow: none;
}

.coming-soon-label {
  display: inline-block;
  margin-top: 8px;
  font-size: 11px;
  font-weight: 500;
  color: var(--text-color-3, #999);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.task-option h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-color);
}

.task-option p {
  margin: 0;
  font-size: 14px;
  color: var(--text-color-2);
}

.project-type-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.project-type-buttons .n-button {
  min-width: 140px;
}

@media (min-width: 768px) {
  .project-type-buttons {
    flex-wrap: nowrap;
  }
}

@media (max-width: 1024px) {
  .main-content {
    grid-template-columns: 1fr;
  }
  
  .mini-brief-section {
    order: -1;
  }
  
  .mini-brief-card {
    position: static;
  }
}

/* Thumbnail styles */
.thumbnail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 16px;
}

.thumbnail-wrapper {
  position: relative;
  width: 100%;
  padding-top: 100%; /* Square aspect ratio */
  background: var(--base-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.thumbnail-wrapper:hover {
  border-color: var(--primary-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.thumbnail-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-close {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background-color 0.2s, transform 0.2s;
  z-index: 10;
}

.thumbnail-close:hover {
  background: rgba(255, 0, 0, 0.9);
  transform: scale(1.1);
}

.thumbnail-close:active {
  transform: scale(0.95);
}
</style>


